import React, { useCallback, useEffect, useState, createElement } from 'react';
import { DollarSignIcon, SearchIcon, AlertCircleIcon, ClockIcon, FileTextIcon, PlusIcon, EditIcon, TrashIcon, UserIcon, CheckCircleIcon, PercentIcon, SettingsIcon, CreditCardIcon, DownloadIcon, FileMinusIcon, ArrowRightLeftIcon, CalculatorIcon, BanIcon, UndoIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { supabaseAdmin } from '../lib/supabase';
import { Invoice, CreditNote } from '../types';
import { useApp } from '../context/AppContext';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import jsPDF from 'jspdf';
import { useAuth } from '../context/AuthContext';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
type TabType = 'facturas' | 'notas-credito' | 'aplicar-nc' | 'historial-nc';
type FormData = {
  numeroFactura: string;
  farmacia: string;
  vendedor: string;
  vendedorId: string;
  fechaEmision: string;
  fechaVencimiento: string;
  subTotal: string;
  itbis: string;
  monto: string;
  saldoPendiente: string;
  estado: Invoice['estado'];
  ppPct: string;
};
type NCFormData = {
  numero: string;
  farmacia: string;
  vendedor: string;
  vendedorId: string;
  monto: string;
  notas: string;
};
const emptyForm: FormData = {
  numeroFactura: '',
  farmacia: '',
  vendedor: '',
  vendedorId: '',
  fechaEmision: new Date().toISOString().split('T')[0],
  fechaVencimiento: '',
  subTotal: '',
  itbis: '',
  monto: '',
  saldoPendiente: '',
  estado: 'pendiente',
  ppPct: ''
};
const emptyNCForm: NCFormData = {
  numero: '',
  farmacia: '',
  vendedor: '',
  vendedorId: '',
  monto: '',
  notas: ''
};
// ── PDF Receipt ───────────────────────────────────────────────────────────────
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png';
async function getLogoBase64(): Promise<string | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = LOGO_URL;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || 100;
    canvas.height = img.naturalHeight || 100;
    canvas.getContext('2d')!.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
export async function downloadReciboPago(farmacia: string, invoicesPaid: {
  numeroFactura: string;
  monto: number;
  abono: number;
  descuento?: number;
}[], totalPagado: number, metodoPago: string) {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a5'
  });
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const ml = 10;
  const mr = 10;
  const fmtNum = (n: number) => new Intl.NumberFormat('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(n);
  const fmtDate = (d: Date) => d.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
  const logoBase64 = await getLogoBase64();
  if (logoBase64) doc.addImage(logoBase64, 'PNG', ml, 4, 20, 20);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('Miladys Rodriguez & Aso.', ml + 24, 12);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('Av. Antonio Guzmán Nº 102 (Altos) • Tels. 809-247-2104 • 809-247-1006', ml + 24, 18);
  doc.text('Fax: 809-247-5098 • Santiago, República Dominicana  •  RNC 130-329291', ml + 24, 23);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('Fecha:', pw - mr - 44, 12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(fmtDate(new Date()), pw - mr - 20, 12);
  doc.setFillColor(220, 235, 255);
  doc.setDrawColor(41, 82, 196);
  doc.setLineWidth(0.6);
  doc.rect(pw - mr - 46, 14, 46, 10, 'FD');
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('RD$ ' + fmtNum(totalPagado), pw - mr - 23, 21, {
    align: 'center'
  });
  const receiptNum = Math.floor(Math.random() * 90000 + 10000).toString();
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(200, 30, 30);
  doc.text(receiptNum, pw - mr, 32, {
    align: 'right'
  });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('NUMERO DE RECIBO  M', ml, 30);
  doc.setDrawColor(13, 27, 70);
  doc.setLineWidth(0.9);
  doc.line(ml, 31.5, pw - mr, 31.5);
  doc.setLineWidth(0.3);
  doc.line(ml, 33, pw - mr, 33);
  let y = 40;
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text('He recibido de', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(farmacia, ml + 34, y);
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  doc.line(ml + 33, y + 1, pw - mr, y + 1);
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text('El valor de', ml, y);
  doc.line(ml + 24, y + 1, pw - mr, y + 1);
  y += 7;
  doc.setFontSize(7.5);
  doc.text('Para ser abonado a su cuenta MILADYS RODRIGUEZ & ASOCIADOS, en la siguiente forma:', ml, y);
  y += 5;
  const cols = ['FACTURA Nº', 'FECHA', 'VALOR', 'CRÉDITO', 'DESCUENTO', 'MONTO'];
  const colW = [35, 28, 32, 30, 30, 35];
  const tableW = 190;
  const rowH = 7.5;
  doc.setFillColor(13, 27, 70);
  doc.rect(ml, y, tableW, rowH, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  let x = ml;
  cols.forEach((col, i) => {
    doc.text(col, x + colW[i] / 2, y + rowH - 2.2, {
      align: 'center'
    });
    x += colW[i];
  });
  y += rowH;
  const totalRows = Math.max(invoicesPaid.length, 5);
  for (let i = 0; i < totalRows; i++) {
    const isEven = i % 2 === 0;
    doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 255);
    doc.rect(ml, y, tableW, rowH, 'F');
    doc.setDrawColor(180, 200, 220);
    doc.setLineWidth(0.2);
    doc.rect(ml, y, tableW, rowH, 'S');
    if (i < invoicesPaid.length) {
      const inv = invoicesPaid[i];
      let cx = ml;
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text(inv.numeroFactura, cx + colW[0] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      cx += colW[0];
      doc.setFont('helvetica', 'normal');
      doc.text(fmtDate(new Date()), cx + colW[1] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      cx += colW[1];
      doc.text(fmtNum(inv.monto), cx + colW[2] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      cx += colW[2];
      doc.text(fmtNum(inv.abono), cx + colW[3] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      cx += colW[3];
      if (inv.descuento && inv.descuento > 0) {
        doc.setTextColor(5, 120, 60);
        doc.text(fmtNum(inv.descuento), cx + colW[4] / 2, y + rowH - 2.2, {
          align: 'center'
        });
        doc.setTextColor(0, 0, 0);
      }
      cx += colW[4];
      const neto = inv.abono - (inv.descuento || 0);
      doc.setFont('helvetica', 'bold');
      doc.text(fmtNum(neto > 0 ? neto : inv.abono), cx + colW[5] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      doc.setFont('helvetica', 'normal');
    }
    y += rowH;
  }
  doc.setDrawColor(13, 27, 70);
  doc.setLineWidth(0.5);
  doc.line(ml, y, ml + tableW, y);
  const subTotalVal = Math.round(totalPagado / 1.18 * 100) / 100;
  const itbisVal = Math.round((totalPagado - subTotalVal) * 100) / 100;
  const summaryRowH = 7.5;
  y += 2; // spacing before summary
  // Sub Total row
  doc.setFillColor(248, 250, 252);
  doc.rect(ml, y, tableW, summaryRowH, 'F');
  doc.setDrawColor(180, 200, 220);
  doc.setLineWidth(0.15);
  doc.rect(ml, y, tableW, summaryRowH, 'S');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('SUB TOTAL:', ml + tableW - 58, y + summaryRowH / 2 + 1, {
    align: 'left'
  });
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('RD$ ' + fmtNum(subTotalVal), ml + tableW - 2, y + summaryRowH / 2 + 1, {
    align: 'right'
  });
  y += summaryRowH;
  // ITBIS row
  doc.setFillColor(248, 250, 252);
  doc.rect(ml, y, tableW, summaryRowH, 'F');
  doc.setDrawColor(180, 200, 220);
  doc.setLineWidth(0.15);
  doc.rect(ml, y, tableW, summaryRowH, 'S');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('ITBIS 18%:', ml + tableW - 58, y + summaryRowH / 2 + 1, {
    align: 'left'
  });
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('RD$ ' + fmtNum(itbisVal), ml + tableW - 2, y + summaryRowH / 2 + 1, {
    align: 'right'
  });
  y += summaryRowH;
  // TOTAL row
  const totalSummaryH = 9;
  doc.setFillColor(220, 235, 255);
  doc.rect(ml, y, tableW, totalSummaryH, 'F');
  doc.setDrawColor(13, 27, 70);
  doc.setLineWidth(0.4);
  doc.rect(ml, y, tableW, totalSummaryH, 'S');
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('TOTAL:', ml + tableW - 58, y + totalSummaryH / 2 + 1, {
    align: 'left'
  });
  doc.text('RD$ ' + fmtNum(totalPagado), ml + tableW - 2, y + totalSummaryH / 2 + 1, {
    align: 'right'
  });
  y += totalSummaryH;
  y += 9;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  doc.text('Efectivo', ml, y);
  doc.line(ml + 16, y + 1, ml + 56, y + 1);
  if (metodoPago === 'efectivo') {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 120, 60);
    doc.text(fmtNum(totalPagado), ml + 17, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
  }
  doc.text('Cheque Nº', ml + 60, y);
  doc.line(ml + 76, y + 1, ml + 110, y + 1);
  if (metodoPago === 'cheque') {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 120, 60);
    doc.text('✓', ml + 77, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
  }
  doc.text('Banco', ml + 112, y);
  doc.line(ml + 122, y + 1, ml + 152, y + 1);
  if (metodoPago === 'transferencia') {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 120, 60);
    doc.text('Transferencia', ml + 123, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
  }
  doc.text('Futurista', ml + 154, y);
  doc.line(ml + 165, y + 1, pw - mr, y + 1);
  y += 14;
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.line(ml, y, ml + 65, y);
  doc.line(pw - mr - 65, y, pw - mr, y);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('Conforme Cliente', ml + 32, y + 5, {
    align: 'center'
  });
  doc.text('Recibido Conforme', pw - mr - 32, y + 5, {
    align: 'center'
  });
  doc.setFillColor(13, 27, 70);
  doc.rect(0, ph - 9, pw, 9, 'F');
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(123, 163, 216);
  doc.text('Miladys Rodriguez & Asoc. © 2025 — Sistema de Control de Vendedores', pw / 2, ph - 3.5, {
    align: 'center'
  });
  doc.save(`recibo-${farmacia.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`);
}
export function CuentasCobrarPage() {
  const {
    clients,
    users,
    orders
  } = useApp();
  const {
    user,
    addActivityLog
  } = useAuth();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const canSeeDiscount = isAdmin || user?.permisos?.descuento_pronto_pago === true || user?.permisos?.descuento_pronto_pago === undefined;
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('facturas');
  // Invoice state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendedorFilter, setVendedorFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDiscountConfigOpen, setIsDiscountConfigOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [facturaError, setFacturaError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  // Credit Notes state
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  // ── Name normalization helpers (used across tabs) ──────────────────────────
  // Aggressive normalization: lowercase, collapse whitespace, strip punctuation & accents
  const normalizeName = (s: string) => s.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') // strip accents
  .replace(/[^a-z0-9\s]/g, '') // strip punctuation
  .replace(/\s+/g, ' ') // collapse whitespace
  .trim();
  // Looser match: checks if two names refer to the same farmacia
  const farmaciaMatch = (a: string, b: string) => {
    if (!a || !b) return false;
    const na = normalizeName(a);
    const nb = normalizeName(b);
    if (na === nb) return true;
    // Fallback: one contains the other (handles partial name differences)
    if (na.length > 3 && nb.length > 3) {
      if (na.includes(nb) || nb.includes(na)) return true;
    }
    // Fallback: compare first N significant characters (handles trailing suffixes like "SRL" vs "EIRL")
    const wordsA = na.split(' ').filter(w => w.length > 1);
    const wordsB = nb.split(' ').filter(w => w.length > 1);
    if (wordsA.length >= 2 && wordsB.length >= 2) {
      // If the first 2 significant words match, consider it a match
      if (wordsA[0] === wordsB[0] && wordsA[1] === wordsB[1]) return true;
    }
    return false;
  };
  // Derived lists — must be after creditNotes useState
  // Collect all farmacia names from clients, invoices, and credit notes
  const allFarmaciaNames = [...clients.map(c => c.nombre), ...invoices.filter(inv => inv.farmacia).map(inv => inv.farmacia), ...creditNotes.filter(nc => nc.farmacia).map(nc => nc.farmacia)];
  // Deduplicate using normalizeName to avoid near-duplicates
  const seenNormalized = new Map<string, string>();
  allFarmaciaNames.forEach(name => {
    const norm = normalizeName(name);
    if (!seenNormalized.has(norm)) {
      seenNormalized.set(norm, name);
    }
  });
  const FARMACIAS = [...seenNormalized.values()].sort();
  const VENDEDORES = users.filter(u => u.activo && u.permisos?.['new-visit'] === true).map(u => u.nombre);
  const [isNCModalOpen, setIsNCModalOpen] = useState(false);
  const [isNCDeleteModalOpen, setIsNCDeleteModalOpen] = useState(false);
  const [editingNC, setEditingNC] = useState<CreditNote | null>(null);
  const [ncFormData, setNCFormData] = useState<NCFormData>(emptyNCForm);
  const [ncError, setNCError] = useState('');
  const [ncPage, setNCPage] = useState(1);
  const [ncSearchQuery, setNCSearchQuery] = useState('');
  // Application state
  const [appFarmacia, setAppFarmacia] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [selectedNCId, setSelectedNCId] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  // NC Applications history state
  const [ncApplications, setNcApplications] = useState<any[]>([]);
  const [anularNCApp, setAnularNCApp] = useState<any | null>(null);
  const [isAnulandoNC, setIsAnulandoNC] = useState(false);
  const [ncAppPage, setNcAppPage] = useState(1);
  // Payment modal state
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentClient, setPaymentClient] = useState('');
  const [paymentAmounts, setPaymentAmounts] = useState<Record<string, number>>({});
  const [paymentMetodo, setPaymentMetodo] = useState('efectivo');
  const [paymentDone, setPaymentDone] = useState(false);
  const [lastPaymentData, setLastPaymentData] = useState<{
    farmacia: string;
    invoices: {
      numeroFactura: string;
      monto: number;
      abono: number;
    }[];
    total: number;
  } | null>(null);
  const [isDownloadingRecibo, setIsDownloadingRecibo] = useState(false);
  const [isCotizacionModalOpen, setIsCotizacionModalOpen] = useState(false);
  const [cotizacionSearch, setCotizacionSearch] = useState('');
  // Discount config
  const [discountPct, setDiscountPct] = useState(() => parseFloat(localStorage.getItem('mr_discount_pct') || '5'));
  const [discountDays, setDiscountDays] = useState(() => parseInt(localStorage.getItem('mr_discount_days') || '60', 10));
  const [discountConfigDraft, setDiscountConfigDraft] = useState(() => ({
    pct: parseFloat(localStorage.getItem('mr_discount_pct') || '5'),
    days: parseInt(localStorage.getItem('mr_discount_days') || '60', 10)
  }));
  useEffect(() => {
    const loadDiscountFromDB = async () => {
      try {
        const {
          data
        } = await supabaseAdmin.from('app_settings').select('key, value').in('key', ['discount_pct', 'discount_days']);
        if (data && data.length > 0) {
          const pctRow = data.find((r: any) => r.key === 'discount_pct');
          const daysRow = data.find((r: any) => r.key === 'discount_days');
          const pct = pctRow ? parseFloat(pctRow.value) : 5;
          const days = daysRow ? parseInt(daysRow.value, 10) : 60;
          setDiscountPct(pct);
          setDiscountDays(days);
          setDiscountConfigDraft({
            pct,
            days
          });
          localStorage.setItem('mr_discount_pct', String(pct));
          localStorage.setItem('mr_discount_days', String(days));
        }
      } catch {}
    };
    loadDiscountFromDB();
  }, []);
  const saveDiscountConfig = async (pct: number, days: number) => {
    try {
      await supabaseAdmin.from('app_settings').upsert([{
        key: 'discount_pct',
        value: String(pct),
        updated_at: new Date().toISOString()
      }, {
        key: 'discount_days',
        value: String(days),
        updated_at: new Date().toISOString()
      }]);
    } catch {}
    localStorage.setItem('mr_discount_pct', String(pct));
    localStorage.setItem('mr_discount_days', String(days));
    setDiscountPct(pct);
    setDiscountDays(days);
    window.dispatchEvent(new CustomEvent('discount-config-updated', {
      detail: {
        pct,
        days
      }
    }));
  };
  const mapInvoice = (row: any): Invoice => {
    const fechaVencimiento = new Date(row.fecha_vencimiento);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let estado: Invoice['estado'] = row.estado;
    if (estado !== 'pagada' && fechaVencimiento < today && row.saldo_pendiente > 0) estado = 'vencida';
    return {
      id: row.id,
      numeroFactura: row.numero_factura,
      farmacia: row.farmacia,
      vendedor: row.vendedor,
      vendedorId: row.vendedor_id || undefined,
      fechaEmision: new Date(row.fecha_emision),
      fechaVencimiento,
      subTotal: row.sub_total || 0,
      itbis: row.itbis || 0,
      monto: row.monto,
      saldoPendiente: row.saldo_pendiente,
      estado,
      ppPct: row.pp_pct != null ? Number(row.pp_pct) : null
    };
  };
  const mapCreditNote = (row: any): CreditNote => ({
    id: row.id,
    numero: row.numero,
    farmacia: row.farmacia,
    vendedor: row.vendedor,
    vendedorId: row.vendedor_id || undefined,
    monto: row.monto,
    montoDisponible: row.monto_disponible,
    facturaAplicada: row.factura_aplicada || null,
    estado: row.estado,
    fechaCreacion: new Date(row.fecha_creacion),
    notas: row.notas || ''
  });
  const loadInvoices = async () => {
    setIsLoading(true);
    try {
      const {
        data
      } = await supabaseAdmin.from('invoices').select('id,numero_factura,farmacia,vendedor,vendedor_id,fecha_emision,fecha_vencimiento,sub_total,itbis,monto,saldo_pendiente,estado,pp_pct').order('fecha_emision', {
        ascending: false
      }).limit(500);
      if (data) {
        const mapped = data.map(mapInvoice);
        setInvoices(mapped);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const toFix = data.filter((row: any) => {
          const venc = new Date(row.fecha_vencimiento);
          return (row.estado === 'pendiente' || row.estado === 'parcial') && venc < today && row.saldo_pendiente > 0;
        });
        if (toFix.length > 0) {
          Promise.all(toFix.map((row: any) => supabaseAdmin.from('invoices').update({
            estado: 'vencida'
          }).eq('id', row.id))).catch(() => {});
        }
      }
    } finally {
      setIsLoading(false);
    }
  };
  const loadCreditNotes = async () => {
    try {
      const {
        data
      } = await supabaseAdmin.from('credit_notes').select('id,numero,farmacia,vendedor,vendedor_id,monto,monto_disponible,factura_aplicada,estado,fecha_creacion,notas').order('fecha_creacion', {
        ascending: false
      }).limit(300);
      if (data) setCreditNotes(data.map(mapCreditNote));
    } catch {}
  };
  const loadNCApplications = async () => {
    try {
      const {
        data
      } = await supabaseAdmin.from('nc_applications').select('*').order('created_at', {
        ascending: false
      }).limit(200);
      if (data) setNcApplications(data);
    } catch {}
  };
  const handleAnularNCApplication = async () => {
    if (!anularNCApp) return;
    setIsAnulandoNC(true);
    try {
      const ncApp = anularNCApp;
      // 1. Restore invoice saldo_pendiente
      const restoredAmount = Number(ncApp.total_original) - Number(ncApp.total_a_pagar);
      const {
        data: currentInvoice
      } = await supabaseAdmin.from('invoices').select('id, saldo_pendiente, monto').eq('id', ncApp.invoice_id).single();
      if (currentInvoice) {
        const newBalance = Math.min(currentInvoice.monto, Number(currentInvoice.saldo_pendiente) + restoredAmount);
        const newStatus = newBalance <= 0 ? 'pagada' : newBalance < currentInvoice.monto ? 'parcial' : 'pendiente';
        await supabaseAdmin.from('invoices').update({
          saldo_pendiente: newBalance,
          estado: newStatus
        }).eq('id', ncApp.invoice_id);
      }
      // 2. Restore credit note monto_disponible
      const ncMontoAplicado = Number(ncApp.nc_monto_aplicado) || 0;
      if (ncMontoAplicado > 0) {
        const {
          data: currentNC
        } = await supabaseAdmin.from('credit_notes').select('id, monto, monto_disponible').eq('id', ncApp.credit_note_id).single();
        if (currentNC) {
          const newNCBalance = Math.min(currentNC.monto, Number(currentNC.monto_disponible) + ncMontoAplicado);
          const newNCStatus = newNCBalance >= currentNC.monto ? 'disponible' : 'parcial';
          await supabaseAdmin.from('credit_notes').update({
            monto_disponible: newNCBalance,
            estado: newNCStatus,
            factura_aplicada: null
          }).eq('id', ncApp.credit_note_id);
        }
      }
      // 3. Propagate to related visits — restore saldo in facturasDetalle
      try {
        const {
          data: relatedVisits
        } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', ncApp.farmacia).not('cobro', 'is', null).limit(500);
        if (relatedVisits && relatedVisits.length > 0) {
          for (const visit of relatedVisits) {
            const cobro = visit.cobro as any;
            if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
            const matchIdx = cobro.facturasDetalle.findIndex((fd: any) => fd.facturaId === ncApp.invoice_id || fd.numeroFactura && fd.numeroFactura === ncApp.invoice_numero);
            if (matchIdx === -1) continue;
            const updatedDetalle = cobro.facturasDetalle.map((fd: any, idx: number) => {
              if (idx !== matchIdx) return fd;
              return {
                ...fd,
                saldoNuevo: currentInvoice ? Math.min(currentInvoice.monto, Number(currentInvoice.saldo_pendiente) + restoredAmount) : fd.saldoAnterior || fd.saldoNuevo,
                estado: 'pendiente'
              };
            });
            await supabaseAdmin.from('visits').update({
              cobro: {
                ...cobro,
                facturasDetalle: updatedDetalle
              }
            }).eq('id', visit.id);
          }
        }
      } catch (propagateErr) {
        console.error('Error propagando anulación NC a visitas:', propagateErr);
      }
      // 4. Delete the nc_application record
      await supabaseAdmin.from('nc_applications').delete().eq('id', ncApp.id);
      // 5. Refresh data
      await loadInvoices();
      await loadCreditNotes();
      await loadNCApplications();
      addActivityLog('Anuló aplicación NC', `NC: ${ncApp.credit_note_numero} | Factura: ${ncApp.invoice_numero} | Farmacia: ${ncApp.farmacia}`);
      setAnularNCApp(null);
    } catch (err) {
      console.error('Error anulando aplicación NC:', err);
    } finally {
      setIsAnulandoNC(false);
    }
  };
  useEffect(() => {
    loadInvoices();
    loadCreditNotes();
    loadNCApplications();
  }, []);
  // Auto-refresh when invoices or credit notes change in Supabase
  const refreshAllData = useCallback(() => {
    loadInvoices();
    loadCreditNotes();
    loadNCApplications();
  }, []);
  useRealtimeRefresh(['invoices', 'credit_notes', 'nc_applications', 'app_settings'], refreshAllData);
  // Invoice filtering
  const filteredInvoices = invoices.filter(inv => {
    if (!isAdmin && inv.vendedorId !== user?.id) return false;
    const matchesSearch = inv.farmacia.toLowerCase().includes(searchQuery.toLowerCase()) || inv.numeroFactura.toLowerCase().includes(searchQuery.toLowerCase()) || inv.vendedor.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inv.estado === statusFilter;
    const matchesVendedor = vendedorFilter === 'all' || inv.vendedor === vendedorFilter;
    return matchesSearch && matchesStatus && matchesVendedor;
  });
  const {
    items: sortedInvoices,
    requestSort,
    sortConfig
  } = useSortableData(filteredInvoices, {
    numeroFactura: i => i.numeroFactura,
    farmacia: i => i.farmacia,
    vendedor: i => i.vendedor,
    fechaVencimiento: i => new Date(i.fechaVencimiento).getTime(),
    subTotal: i => i.subTotal,
    itbis: i => i.itbis,
    monto: i => i.monto,
    saldoPendiente: i => i.saldoPendiente,
    estado: i => i.estado
  });
  const finalInvoices = sortConfig ? sortedInvoices : [...sortedInvoices].sort((a, b) => new Date(b.fechaEmision).getTime() - new Date(a.fechaEmision).getTime());
  const totalPendiente = finalInvoices.reduce((sum, inv) => sum + inv.saldoPendiente, 0);
  const totalVencido = finalInvoices.filter(inv => inv.estado === 'vencida').reduce((sum, inv) => sum + inv.saldoPendiente, 0);
  const facturasPendientes = finalInvoices.filter(inv => inv.saldoPendiente > 0).length;
  const totalPages = Math.ceil(finalInvoices.length / ITEMS_PER_PAGE);
  const paginatedInvoices = finalInvoices.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // Credit Notes filtering
  const filteredNC = creditNotes.filter(nc => {
    if (!isAdmin && nc.vendedorId !== user?.id) return false;
    return nc.farmacia.toLowerCase().includes(ncSearchQuery.toLowerCase()) || nc.numero.toLowerCase().includes(ncSearchQuery.toLowerCase());
  });
  const ncTotalPages = Math.ceil(filteredNC.length / ITEMS_PER_PAGE);
  const paginatedNC = filteredNC.slice((ncPage - 1) * ITEMS_PER_PAGE, ncPage * ITEMS_PER_PAGE);
  const ncDisponibles = filteredNC.filter(nc => nc.estado === 'disponible');
  const ncAplicadas = filteredNC.filter(nc => nc.estado === 'aplicada');
  const ncMontoDisponible = ncDisponibles.reduce((sum, nc) => sum + nc.montoDisponible, 0);
  // Application tab data
  const appInvoices = invoices.filter(inv => farmaciaMatch(inv.farmacia, appFarmacia) && inv.saldoPendiente > 0 && inv.estado !== 'pagada');
  const appCreditNotes = creditNotes.filter(nc => farmaciaMatch(nc.farmacia, appFarmacia) && (nc.estado === 'disponible' || nc.estado === 'parcial') && nc.montoDisponible > 0);
  const selectedInvoice = appInvoices.find(inv => inv.id === selectedInvoiceId) || null;
  const selectedNC = appCreditNotes.find(nc => nc.id === selectedNCId) || null;
  // Calculation logic (BR-03 to BR-07)
  // When multiple NCs are applied, use the CURRENT saldo to derive remaining subtotal
  const calculateApplication = () => {
    if (!selectedInvoice) return null;
    const subtotalOriginal = selectedInvoice.subTotal;
    const itbisOriginal = selectedInvoice.itbis;
    const totalOriginal = selectedInvoice.monto;
    // If saldo < monto, previous NCs have already reduced the subtotal
    // Remaining subtotal = saldoPendiente - itbis (ITBIS never changes)
    const currentSaldo = selectedInvoice.saldoPendiente;
    const remainingSubtotal = currentSaldo < totalOriginal ? Math.max(0, currentSaldo - itbisOriginal) : subtotalOriginal;
    const ncMonto = selectedNC ? Math.min(selectedNC.montoDisponible, remainingSubtotal) : 0; // BR-07: cap at remaining subtotal
    const subtotalAjustado = Math.max(0, remainingSubtotal - ncMonto); // BR-03: NC applies to subtotal only
    // No discount applied in this section — only credit note
    const descuentoMonto = 0;
    const subtotalFinal = subtotalAjustado;
    const itbisFinal = itbisOriginal; // BR-05: ITBIS unchanged
    const totalAPagar = subtotalFinal + itbisFinal; // BR-06
    return {
      subtotalOriginal,
      itbisOriginal,
      totalOriginal,
      ncMonto,
      subtotalAjustado,
      descuentoPct: 0,
      descuentoMonto,
      subtotalFinal,
      itbisFinal,
      totalAPagar,
      remainingSubtotal // track for display
    };
  };
  const calculation = calculateApplication();
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const getStatusBadge = (status: Invoice['estado']) => {
    switch (status) {
      case 'pagada':
        return <Badge variant="success" data-id="element-1019">Pagada</Badge>;
      case 'parcial':
        return <Badge variant="warning" data-id="element-1020">Parcial</Badge>;
      case 'vencida':
        return <Badge variant="danger" data-id="element-1021">Vencida</Badge>;
      default:
        return <Badge variant="info" data-id="element-1022">Pendiente</Badge>;
    }
  };
  const getNCStatusBadge = (status: CreditNote['estado']) => {
    switch (status) {
      case 'disponible':
        return <Badge variant="success" data-id="element-1023">Disponible</Badge>;
      case 'aplicada':
        return <Badge variant="info" data-id="element-1024">Aplicada</Badge>;
      case 'parcial':
        return <Badge variant="warning" data-id="element-1025">Parcial</Badge>;
      default:
        return <Badge variant="default" data-id="element-1026">{status}</Badge>;
    }
  };
  // Invoice CRUD
  const openAddModal = () => {
    setEditingInvoice(null);
    setFacturaError('');
    setFormData({
      ...emptyForm,
      numeroFactura: '',
      farmacia: FARMACIAS[0] || '',
      vendedor: user?.nombre || VENDEDORES[0] || '',
      vendedorId: user?.id || users.find(u => u.activo)?.id || ''
    });
    setIsModalOpen(true);
  };
  const openEditModal = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      numeroFactura: invoice.numeroFactura,
      farmacia: invoice.farmacia,
      vendedor: invoice.vendedor,
      vendedorId: invoice.vendedorId || '',
      fechaEmision: new Date(invoice.fechaEmision).toISOString().split('T')[0],
      fechaVencimiento: new Date(invoice.fechaVencimiento).toISOString().split('T')[0],
      subTotal: String(invoice.subTotal || 0),
      itbis: String(invoice.itbis || 0),
      monto: String(invoice.monto),
      saldoPendiente: String(invoice.saldoPendiente),
      estado: invoice.estado,
      ppPct: invoice.ppPct != null ? String(invoice.ppPct) : ''
    });
    setFacturaError('');
    setIsModalOpen(true);
  };
  const handleSave = async () => {
    if (!formData.numeroFactura || !formData.monto || !formData.fechaVencimiento) return;
    const isDuplicate = invoices.some(inv => inv.numeroFactura.trim().toLowerCase() === formData.numeroFactura.trim().toLowerCase() && inv.id !== editingInvoice?.id);
    if (isDuplicate) {
      setFacturaError(`El número de factura "${formData.numeroFactura}" ya existe.`);
      return;
    }
    setFacturaError('');
    setIsSaving(true);
    try {
      let finalEstado = formData.estado;
      const fechaVenc = new Date(formData.fechaVencimiento);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const saldoPend = Number(formData.saldoPendiente);
      if (finalEstado !== 'pagada' && fechaVenc < today && saldoPend > 0) finalEstado = 'vencida';
      if (finalEstado === 'vencida' && fechaVenc >= today) finalEstado = saldoPend < Number(formData.monto) && saldoPend > 0 ? 'parcial' : 'pendiente';
      const payload = {
        numero_factura: formData.numeroFactura,
        farmacia: formData.farmacia,
        vendedor: formData.vendedor,
        vendedor_id: formData.vendedorId || null,
        fecha_emision: new Date(formData.fechaEmision).toISOString(),
        fecha_vencimiento: new Date(formData.fechaVencimiento).toISOString(),
        sub_total: Number(formData.subTotal) || 0,
        itbis: Number(formData.itbis) || 0,
        monto: Number(formData.monto),
        saldo_pendiente: saldoPend,
        estado: finalEstado,
        pp_pct: formData.ppPct !== '' ? Number(formData.ppPct) : null
      };
      if (editingInvoice) {
        const {
          data
        } = await supabaseAdmin.from('invoices').update(payload).eq('id', editingInvoice.id).select().single();
        if (data) setInvoices(prev => prev.map(inv => inv.id === editingInvoice.id ? mapInvoice(data) : inv));
        addActivityLog('Editó factura', `Factura: ${formData.numeroFactura} | Farmacia: ${formData.farmacia} | Monto: RD${formData.monto}`);
        // ── Propagate invoice changes to related visits ──────────────────────
        // Find visits that reference this invoice in their cobro.facturasDetalle
        // so VisitsPage, ReportsPage, and CollectionsPage show updated data.
        try {
          const {
            data: relatedVisits
          } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', editingInvoice.farmacia).not('cobro', 'is', null).limit(500);
          if (relatedVisits && relatedVisits.length > 0) {
            const oldNumero = editingInvoice.numeroFactura;
            const newNumero = formData.numeroFactura;
            for (const visit of relatedVisits) {
              const cobro = visit.cobro as any;
              if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
              // Check if this visit references the edited invoice
              const matchIdx = cobro.facturasDetalle.findIndex((fd: any) => fd.facturaId === editingInvoice.id || fd.numeroFactura && fd.numeroFactura === oldNumero);
              if (matchIdx === -1) continue;
              // Update the matching facturasDetalle entry
              const updatedDetalle = cobro.facturasDetalle.map((fd: any, idx: number) => {
                if (idx !== matchIdx) return fd;
                return {
                  ...fd,
                  numeroFactura: newNumero,
                  saldoNuevo: saldoPend,
                  estado: finalEstado,
                  subTotal: Number(formData.subTotal) || fd.subTotal || 0,
                  itbis: Number(formData.itbis) || fd.itbis || 0
                };
              });
              // Update cobro.numeroFactura string if invoice number changed
              let updatedNumeroFactura = cobro.numeroFactura || '';
              if (oldNumero !== newNumero && updatedNumeroFactura.includes(oldNumero)) {
                updatedNumeroFactura = updatedNumeroFactura.split(',').map((s: string) => s.trim() === oldNumero ? newNumero : s.trim()).join(', ');
              }
              await supabaseAdmin.from('visits').update({
                cobro: {
                  ...cobro,
                  facturasDetalle: updatedDetalle,
                  numeroFactura: updatedNumeroFactura
                }
              }).eq('id', visit.id);
            }
          }
        } catch (propagateErr) {
          console.error('Error propagando cambios a visitas:', propagateErr);
          // Don't block the save — invoice was already saved successfully
        }
      } else {
        const {
          data
        } = await supabaseAdmin.from('invoices').insert(payload).select().single();
        if (data) setInvoices(prev => [mapInvoice(data), ...prev]);
        addActivityLog('Creó factura', `Factura: ${formData.numeroFactura} | Farmacia: ${formData.farmacia} | Monto: RD${formData.monto}`);
      }
      setIsModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingInvoice) return;
    addActivityLog('Eliminó factura', `Factura: ${editingInvoice.numeroFactura} | Farmacia: ${editingInvoice.farmacia}`);
    await supabaseAdmin.from('invoices').delete().eq('id', editingInvoice.id);
    setInvoices(prev => prev.filter(inv => inv.id !== editingInvoice.id));
    // ── Propagate deletion to related visits ─────────────────────────────
    try {
      const {
        data: relatedVisits
      } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', editingInvoice.farmacia).not('cobro', 'is', null).limit(500);
      if (relatedVisits && relatedVisits.length > 0) {
        for (const visit of relatedVisits) {
          const cobro = visit.cobro as any;
          if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
          const hadMatch = cobro.facturasDetalle.some((fd: any) => fd.facturaId === editingInvoice.id || fd.numeroFactura && fd.numeroFactura === editingInvoice.numeroFactura);
          if (!hadMatch) continue;
          // Remove the deleted invoice from facturasDetalle
          const updatedDetalle = cobro.facturasDetalle.filter((fd: any) => fd.facturaId !== editingInvoice.id && fd.numeroFactura !== editingInvoice.numeroFactura);
          // Remove from cobro.numeroFactura string
          let updatedNumeroFactura = cobro.numeroFactura || '';
          if (updatedNumeroFactura.includes(editingInvoice.numeroFactura)) {
            updatedNumeroFactura = updatedNumeroFactura.split(',').map((s: string) => s.trim()).filter((s: string) => s !== editingInvoice.numeroFactura).join(', ');
          }
          await supabaseAdmin.from('visits').update({
            cobro: {
              ...cobro,
              facturasDetalle: updatedDetalle,
              numeroFactura: updatedNumeroFactura
            }
          }).eq('id', visit.id);
        }
      }
    } catch (err) {
      console.error('Error propagando eliminación a visitas:', err);
    }
    setIsDeleteModalOpen(false);
    setIsModalOpen(false);
  };
  // Credit Note CRUD
  const openAddNCModal = () => {
    setEditingNC(null);
    setNCError('');
    const existing = creditNotes.map(nc => {
      const match = nc.numero.match(/^NC-(\d+)$/i);
      return match ? parseInt(match[1], 10) : 0;
    }).filter(n => n > 0);
    const nextNum = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    const nextNC = `NC-${String(nextNum).padStart(5, '0')}`;
    setNCFormData({
      ...emptyNCForm,
      numero: nextNC,
      farmacia: FARMACIAS[0] || '',
      vendedor: user?.nombre || VENDEDORES[0] || '',
      vendedorId: user?.id || users.find(u => u.activo)?.id || ''
    });
    setIsNCModalOpen(true);
  };
  const openEditNCModal = (nc: CreditNote) => {
    setEditingNC(nc);
    setNCFormData({
      numero: nc.numero,
      farmacia: nc.farmacia,
      vendedor: nc.vendedor,
      vendedorId: nc.vendedorId || '',
      monto: String(nc.monto),
      notas: nc.notas || ''
    });
    setNCError('');
    setIsNCModalOpen(true);
  };
  const handleSaveNC = async () => {
    if (!ncFormData.numero || !ncFormData.monto || !ncFormData.farmacia) return;
    const isDuplicate = creditNotes.some(nc => nc.numero.trim().toLowerCase() === ncFormData.numero.trim().toLowerCase() && nc.id !== editingNC?.id);
    if (isDuplicate) {
      setNCError(`El número de NC "${ncFormData.numero}" ya existe.`);
      return;
    }
    setNCError('');
    setIsSaving(true);
    try {
      const monto = Number(ncFormData.monto) || 0;
      const payload = {
        numero: ncFormData.numero,
        farmacia: ncFormData.farmacia,
        vendedor: ncFormData.vendedor,
        vendedor_id: ncFormData.vendedorId || null,
        monto,
        monto_disponible: editingNC ? editingNC.montoDisponible : monto,
        estado: 'disponible',
        notas: ncFormData.notas
      };
      if (editingNC) {
        const {
          data
        } = await supabaseAdmin.from('credit_notes').update(payload).eq('id', editingNC.id).select().single();
        if (data) setCreditNotes(prev => prev.map(nc => nc.id === editingNC.id ? mapCreditNote(data) : nc));
      } else {
        const {
          data
        } = await supabaseAdmin.from('credit_notes').insert(payload).select().single();
        if (data) setCreditNotes(prev => [mapCreditNote(data), ...prev]);
      }
      addActivityLog(editingNC ? 'Editó nota de crédito' : 'Creó nota de crédito', `NC: ${ncFormData.numero} | Farmacia: ${ncFormData.farmacia} | Monto: RD${ncFormData.monto}`);
      setIsNCModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDeleteNC = async () => {
    if (!editingNC) return;
    addActivityLog('Eliminó nota de crédito', `NC: ${editingNC.numero} | Farmacia: ${editingNC.farmacia}`);
    await supabaseAdmin.from('credit_notes').delete().eq('id', editingNC.id);
    setCreditNotes(prev => prev.filter(nc => nc.id !== editingNC.id));
    setIsNCDeleteModalOpen(false);
    setIsNCModalOpen(false);
  };
  // Apply NC to Invoice
  const handleApplyNC = async () => {
    if (!selectedInvoice || !calculation) return;
    setIsApplying(true);
    try {
      // Record the application — only if an NC was selected
      // (credit_note_id is NOT NULL in the DB schema, so we can't insert null)
      if (selectedNC && selectedNC.id) {
        const {
          error: ncAppError
        } = await supabaseAdmin.from('nc_applications').insert({
          credit_note_id: selectedNC.id,
          credit_note_numero: selectedNC.numero,
          invoice_id: selectedInvoice.id,
          invoice_numero: selectedInvoice.numeroFactura,
          farmacia: selectedInvoice.farmacia,
          subtotal_original: calculation.subtotalOriginal,
          itbis_original: calculation.itbisOriginal,
          total_original: calculation.totalOriginal,
          nc_monto_aplicado: calculation.ncMonto,
          subtotal_ajustado: calculation.subtotalAjustado,
          descuento_pct: calculation.descuentoPct,
          descuento_monto: calculation.descuentoMonto,
          subtotal_final: calculation.subtotalFinal,
          itbis_final: calculation.itbisFinal,
          total_a_pagar: calculation.totalAPagar,
          aplicado_por: user?.nombre || 'Sistema'
        });
        if (ncAppError) {
          console.error('Error guardando nc_applications:', ncAppError.message);
        }
      }
      // Update invoice balance — totalAPagar is already based on current saldo
      const newBalance = Math.max(0, calculation.totalAPagar);
      const newStatus = newBalance <= 0 ? 'pagada' : newBalance < selectedInvoice.monto ? 'parcial' : 'pendiente';
      await supabaseAdmin.from('invoices').update({
        saldo_pendiente: newBalance,
        estado: newStatus
      }).eq('id', selectedInvoice.id);
      // Update credit note if used
      if (selectedNC && calculation.ncMonto > 0) {
        const newNCBalance = selectedNC.montoDisponible - calculation.ncMonto;
        const newNCStatus = newNCBalance <= 0 ? 'aplicada' : 'parcial';
        await supabaseAdmin.from('credit_notes').update({
          monto_disponible: Math.max(0, newNCBalance),
          estado: newNCStatus,
          factura_aplicada: selectedInvoice.numeroFactura
        }).eq('id', selectedNC.id);
      }
      // ── Propagate NC application changes to related visits ─────────────
      try {
        const newSaldo = newBalance;
        const {
          data: relatedVisits
        } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', selectedInvoice.farmacia).not('cobro', 'is', null).limit(500);
        if (relatedVisits && relatedVisits.length > 0) {
          for (const visit of relatedVisits) {
            const cobro = visit.cobro as any;
            if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
            const matchIdx = cobro.facturasDetalle.findIndex((fd: any) => fd.facturaId === selectedInvoice.id || fd.numeroFactura && fd.numeroFactura === selectedInvoice.numeroFactura);
            if (matchIdx === -1) continue;
            const updatedDetalle = cobro.facturasDetalle.map((fd: any, idx: number) => {
              if (idx !== matchIdx) return fd;
              return {
                ...fd,
                saldoNuevo: newSaldo,
                estado: newStatus
              };
            });
            await supabaseAdmin.from('visits').update({
              cobro: {
                ...cobro,
                facturasDetalle: updatedDetalle
              }
            }).eq('id', visit.id);
          }
        }
      } catch (propagateErr) {
        console.error('Error propagando NC a visitas:', propagateErr);
      }
      await loadInvoices();
      await loadCreditNotes();
      addActivityLog('Aplicó nota de crédito', `NC: ${selectedNC?.numero || 'N/A'} → Factura: ${selectedInvoice.numeroFactura} | Farmacia: ${selectedInvoice.farmacia} | Monto NC: RD${calculation.ncMonto.toFixed(2)}`);
      setApplicationSuccess(true);
      setSelectedInvoiceId(null);
      setSelectedNCId(null);
    } finally {
      setIsApplying(false);
    }
  };
  // Payment modal
  const openPaymentModal = () => {
    setPaymentClient('');
    setPaymentAmounts({});
    setPaymentMetodo('efectivo');
    setPaymentDone(false);
    setLastPaymentData(null);
    setIsPaymentModalOpen(true);
  };
  const clientInvoices = invoices.filter(inv => farmaciaMatch(inv.farmacia, paymentClient) && inv.saldoPendiente > 0 && inv.estado !== 'pagada');
  const handlePaymentAmountChange = (invoiceId: string, amount: string) => {
    const val = parseFloat(amount);
    if (isNaN(val) || val < 0) {
      const next = {
        ...paymentAmounts
      };
      delete next[invoiceId];
      setPaymentAmounts(next);
      return;
    }
    setPaymentAmounts(prev => ({
      ...prev,
      [invoiceId]: val
    }));
  };
  const totalPayment = Object.values(paymentAmounts).reduce((a, b) => a + b, 0);
  const handleProcessPayment = async () => {
    if (totalPayment <= 0) return;
    setIsSaving(true);
    try {
      const updates = Object.entries(paymentAmounts).map(async ([invoiceId, amount]) => {
        const invoice = invoices.find(i => i.id === invoiceId);
        if (!invoice) return;
        const newBalance = Math.max(0, invoice.saldoPendiente - amount);
        const newStatus = newBalance <= 0 ? 'pagada' : newBalance < invoice.monto ? 'parcial' : 'pendiente';
        const {
          data
        } = await supabaseAdmin.from('invoices').update({
          saldo_pendiente: newBalance,
          estado: newStatus
        }).eq('id', invoiceId).select().single();
        return data ? mapInvoice(data) : null;
      });
      const results = await Promise.all(updates);
      const updatedInvoices = results.filter(Boolean) as Invoice[];
      setInvoices(prev => prev.map(inv => {
        const u = updatedInvoices.find(u => u.id === inv.id);
        return u || inv;
      }));
      const paidInvoices = Object.entries(paymentAmounts).filter(([, amt]) => amt > 0).map(([invoiceId, abono]) => {
        const inv = invoices.find(i => i.id === invoiceId);
        return {
          numeroFactura: inv?.numeroFactura || '',
          monto: inv?.monto || 0,
          abono
        };
      });
      // ── Propagate payment changes to related visits ────────────────────
      try {
        const {
          data: relatedVisits
        } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', paymentClient).not('cobro', 'is', null).limit(500);
        if (relatedVisits && relatedVisits.length > 0) {
          for (const visit of relatedVisits) {
            const cobro = visit.cobro as any;
            if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
            let changed = false;
            const updatedDetalle = cobro.facturasDetalle.map((fd: any) => {
              // Find if this factura was part of the payment
              const matchedUpdate = updatedInvoices.find(u => u.id === fd.facturaId || fd.numeroFactura && u.numeroFactura === fd.numeroFactura);
              if (!matchedUpdate) return fd;
              changed = true;
              return {
                ...fd,
                saldoNuevo: matchedUpdate.saldoPendiente,
                estado: matchedUpdate.estado
              };
            });
            if (!changed) continue;
            await supabaseAdmin.from('visits').update({
              cobro: {
                ...cobro,
                facturasDetalle: updatedDetalle
              }
            }).eq('id', visit.id);
          }
        }
      } catch (propagateErr) {
        console.error('Error propagando pago a visitas:', propagateErr);
      }
      setLastPaymentData({
        farmacia: paymentClient,
        invoices: paidInvoices,
        total: totalPayment
      });
      addActivityLog('Registró pago múltiple', `Farmacia: ${paymentClient} | Total: RD${totalPayment.toFixed(2)} | Facturas: ${paidInvoices.map(i => i.numeroFactura).join(', ')}`);
      setPaymentDone(true);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDownloadRecibo = async () => {
    if (!lastPaymentData) return;
    setIsDownloadingRecibo(true);
    try {
      await downloadReciboPago(lastPaymentData.farmacia, lastPaymentData.invoices, lastPaymentData.total, paymentMetodo);
    } finally {
      setIsDownloadingRecibo(false);
    }
  };
  return <div className="space-y-6" data-id="element-1027">
      <PageHeader title="Cuentas por Cobrar" subtitle="Gestión de facturas, notas de crédito y cobros" icon={<DollarSignIcon className="w-6 h-6" data-id="element-1029" />} data-id="element-1028">
        <div className="flex flex-wrap gap-2" data-id="element-1030">
          {canSeeDiscount && <Button variant="secondary" leftIcon={<SettingsIcon className="w-4 h-4" data-id="element-1032" />} onClick={() => {
          setDiscountConfigDraft({
            pct: discountPct,
            days: discountDays
          });
          setIsDiscountConfigOpen(true);
        }} data-id="element-1031">
              Config. Descuento
            </Button>}
          <Button variant="secondary" leftIcon={<CreditCardIcon className="w-4 h-4" data-id="element-1034" />} onClick={openPaymentModal} data-id="element-1033">
            Registrar Pago
          </Button>
        </div>
      </PageHeader>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit" data-id="element-1035">
        <button onClick={() => setActiveTab('facturas')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${activeTab === 'facturas' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1036">
          <FileTextIcon className="w-4 h-4" data-id="element-1037" />
          Facturas
        </button>
        <button onClick={() => setActiveTab('notas-credito')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${activeTab === 'notas-credito' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1038">
          <FileMinusIcon className="w-4 h-4" data-id="element-1039" />
          Notas de Crédito
        </button>
        <button onClick={() => {
        setActiveTab('aplicar-nc');
        setApplicationSuccess(false);
      }} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${activeTab === 'aplicar-nc' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1040">
          <ArrowRightLeftIcon className="w-4 h-4" data-id="element-1041" />
          Aplicar NC
        </button>
        <button onClick={() => setActiveTab('historial-nc')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${activeTab === 'historial-nc' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1042">
          <ClockIcon className="w-4 h-4" data-id="element-1043" />
          Historial NC
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: FACTURAS */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'facturas' && <>
          {canSeeDiscount && <div className="flex items-center gap-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl" data-id="element-1044">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1045">
                <PercentIcon className="w-5 h-5 text-emerald-600" data-id="element-1046" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-1047">
                <p className="text-sm font-semibold text-slate-900" data-id="element-1048">
                  Política de Descuento por Pronto Pago
                </p>
                <p className="text-xs text-slate-600 mt-0.5" data-id="element-1049">
                  <strong className="text-emerald-700" data-id="element-1050">
                    {discountPct}% de descuento
                  </strong>{' '}
                  para cobros dentro de los primeros{' '}
                  <strong className="text-emerald-700" data-id="element-1051">
                    {discountDays} días
                  </strong>{' '}
                  desde la emisión.
                </p>
              </div>
            </div>}

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1052">
            <StatsCard title="Total por Cobrar" value={formatCurrency(totalPendiente)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-1054" />} variant="primary" data-id="element-1053" />
            <StatsCard title="Saldo Vencido" value={formatCurrency(totalVencido)} icon={<AlertCircleIcon className="w-5 h-5" data-id="element-1056" />} variant="danger" data-id="element-1055" />
            <StatsCard title="Facturas Pendientes" value={facturasPendientes} icon={<ClockIcon className="w-5 h-5" data-id="element-1058" />} variant="warning" data-id="element-1057" />
          </div>

          <div className="flex flex-col sm:flex-row gap-3" data-id="element-1059">
            <div className="relative flex-1" data-id="element-1060">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1061" />
              <input type="text" placeholder="Buscar por farmacia, factura o vendedor..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-1062" />
            </div>
            <select value={vendedorFilter} onChange={e => setVendedorFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-1063">
              <option value="all" data-id="element-1064">Todos los vendedores</option>
              {VENDEDORES.map(v => <option key={v} value={v} data-id="element-1065">
                  {v}
                </option>)}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-1066">
              <option value="all" data-id="element-1067">Todos los estados</option>
              <option value="pendiente" data-id="element-1068">Pendiente</option>
              <option value="parcial" data-id="element-1069">Pago Parcial</option>
              <option value="vencida" data-id="element-1070">Vencida</option>
              <option value="pagada" data-id="element-1071">Pagada</option>
            </select>
            <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1073" />} onClick={openAddModal} data-id="element-1072">
              Nueva Factura
            </Button>
          </div>

          <Card padding="none" data-id="element-1074">
            {filteredInvoices.length === 0 ? <div className="p-16 text-center" data-id="element-1075">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-1076">
                  <FileTextIcon className="w-8 h-8 text-slate-400" data-id="element-1077" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-1078">
                  No hay facturas
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-1079">
                  No se encontraron facturas con los filtros aplicados
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-1080">
                <table className="w-full" data-id="element-1081">
                  <thead data-id="element-1082">
                    <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-1083">
                      <SortableTableHeader label="Factura" sortKey="numeroFactura" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1084" />
                      <SortableTableHeader label="Farmacia" sortKey="farmacia" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1085" />
                      <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1086" />
                      <SortableTableHeader label="Vencimiento" sortKey="fechaVencimiento" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1087" />
                      <SortableTableHeader label="Sub Total" sortKey="subTotal" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1088" />
                      <SortableTableHeader label="ITBIS" sortKey="itbis" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1089" />
                      <SortableTableHeader label="Monto" sortKey="monto" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1090" />
                      <SortableTableHeader label="Saldo" sortKey="saldoPendiente" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1091" />
                      <SortableTableHeader label="Estado" sortKey="estado" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1092" />
                      <th className="px-5 py-4 w-20" data-id="element-1093"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-1094">
                    {paginatedInvoices.map(invoice => <tr key={invoice.id} className={`hover:bg-slate-50/50 transition-colors ${invoice.estado === 'vencida' ? 'bg-rose-50/30' : ''}`} data-id="element-1095">
                        <td className="px-5 py-4 font-medium text-slate-900" data-id="element-1096">
                          {invoice.numeroFactura}
                        </td>
                        <td className="px-5 py-4 font-medium text-slate-900" data-id="element-1097">
                          {invoice.farmacia}
                        </td>
                        <td className="px-5 py-4" data-id="element-1098">
                          <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-1099">
                            <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-1100" />
                            {invoice.vendedor}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-600" data-id="element-1101">
                          {formatDate(invoice.fechaVencimiento)}
                        </td>
                        <td className="px-5 py-4 text-right text-slate-600 text-sm" data-id="element-1102">
                          {formatCurrency(invoice.subTotal)}
                        </td>
                        <td className="px-5 py-4 text-right text-slate-600 text-sm" data-id="element-1103">
                          {formatCurrency(invoice.itbis)}
                        </td>
                        <td className="px-5 py-4 text-right text-slate-700" data-id="element-1104">
                          {formatCurrency(invoice.monto)}
                        </td>
                        <td className="px-5 py-4 text-right font-bold text-slate-900" data-id="element-1105">
                          {formatCurrency(invoice.saldoPendiente)}
                        </td>
                        <td className="px-5 py-4 text-center" data-id="element-1106">
                          {getStatusBadge(invoice.estado)}
                        </td>
                        <td className="px-5 py-4" data-id="element-1107">
                          <div className="flex items-center gap-1" data-id="element-1108">
                            <button onClick={() => openEditModal(invoice)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1109">
                              <EditIcon className="w-4 h-4" data-id="element-1110" />
                            </button>
                            <button onClick={() => {
                      setEditingInvoice(invoice);
                      setIsDeleteModalOpen(true);
                    }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-1111">
                              <TrashIcon className="w-4 h-4" data-id="element-1112" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </Card>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredInvoices.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-1113" />
        </>}

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: NOTAS DE CRÉDITO */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'notas-credito' && <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1114">
            <StatsCard title="NC Disponibles" value={ncDisponibles.length} icon={<FileMinusIcon className="w-5 h-5" data-id="element-1116" />} variant="success" data-id="element-1115" />
            <StatsCard title="NC Aplicadas" value={ncAplicadas.length} icon={<CheckCircleIcon className="w-5 h-5" data-id="element-1118" />} variant="primary" data-id="element-1117" />
            <StatsCard title="Monto Disponible" value={formatCurrency(ncMontoDisponible)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-1120" />} variant="warning" data-id="element-1119" />
          </div>

          <div className="flex flex-col sm:flex-row gap-3" data-id="element-1121">
            <div className="relative flex-1" data-id="element-1122">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1123" />
              <input type="text" placeholder="Buscar por farmacia o número de NC..." value={ncSearchQuery} onChange={e => setNCSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-1124" />
            </div>
            <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1126" />} onClick={openAddNCModal} data-id="element-1125">
              Nueva NC
            </Button>
          </div>

          <Card padding="none" data-id="element-1127">
            {filteredNC.length === 0 ? <div className="p-16 text-center" data-id="element-1128">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-1129">
                  <FileMinusIcon className="w-8 h-8 text-slate-400" data-id="element-1130" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-1131">
                  No hay notas de crédito
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-1132">
                  Crea una nueva nota de crédito para comenzar
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-1133">
                <table className="w-full" data-id="element-1134">
                  <thead data-id="element-1135">
                    <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-1136">
                      <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1137">
                        Número
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1138">
                        Cód. Cliente
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1139">
                        Farmacia
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1140">
                        Vendedor
                      </th>
                      <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-1141">
                        Monto Original
                      </th>
                      <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-1142">
                        Disponible
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1143">
                        Factura Aplicada
                      </th>
                      <th className="px-5 py-4 text-center text-xs font-semibold text-slate-500 uppercase" data-id="element-1144">
                        Estado
                      </th>
                      <th className="px-5 py-4 w-20" data-id="element-1145"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-1146">
                    {paginatedNC.map(nc => <tr key={nc.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-1147">
                        <td className="px-5 py-4 font-medium text-slate-900" data-id="element-1148">
                          {nc.numero}
                        </td>
                        <td className="px-5 py-4" data-id="element-1149">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-mono font-semibold bg-slate-100 text-slate-700" data-id="element-1150">
                            {clients.find(c => c.nombre === nc.farmacia)?.codigo || '—'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-slate-700" data-id="element-1151">
                          {nc.farmacia}
                        </td>
                        <td className="px-5 py-4" data-id="element-1152">
                          <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-1153">
                            <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-1154" />
                            {nc.vendedor}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right text-rose-600 font-medium" data-id="element-1155">
                          -{formatCurrency(nc.monto)}
                        </td>
                        <td className="px-5 py-4 text-right font-bold text-slate-900" data-id="element-1156">
                          {formatCurrency(nc.montoDisponible)}
                        </td>
                        <td className="px-5 py-4 text-slate-600" data-id="element-1157">
                          {nc.facturaAplicada || '—'}
                        </td>
                        <td className="px-5 py-4 text-center" data-id="element-1158">
                          {getNCStatusBadge(nc.estado)}
                        </td>
                        <td className="px-5 py-4" data-id="element-1159">
                          <div className="flex items-center gap-1" data-id="element-1160">
                            <button onClick={() => openEditNCModal(nc)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1161">
                              <EditIcon className="w-4 h-4" data-id="element-1162" />
                            </button>
                            <button onClick={() => {
                      setEditingNC(nc);
                      setIsNCDeleteModalOpen(true);
                    }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-1163">
                              <TrashIcon className="w-4 h-4" data-id="element-1164" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </Card>
          <Pagination currentPage={ncPage} totalPages={ncTotalPages} onPageChange={setNCPage} totalItems={filteredNC.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-1165" />
        </>}

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: APLICAR NC */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'aplicar-nc' && <>
          {applicationSuccess ? <Card className="text-center py-12" data-id="element-1166">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1167">
                <CheckCircleIcon className="w-10 h-10 text-emerald-600" data-id="element-1168" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-1169">
                ¡Aplicación Exitosa!
              </h3>
              <p className="text-slate-600 mb-6" data-id="element-1170">
                La nota de crédito y descuento han sido aplicados correctamente.
              </p>
              <Button onClick={() => {
          setApplicationSuccess(false);
          setAppFarmacia('');
        }} data-id="element-1171">
                Nueva Aplicación
              </Button>
            </Card> : <>
              {/* Available NCs Summary — only shown after selecting a farmacia */}
              {appFarmacia && (() => {
          const farmaciaAvailableNCs = creditNotes.filter(nc => (nc.estado === 'disponible' || nc.estado === 'parcial') && nc.montoDisponible > 0 && farmaciaMatch(nc.farmacia, appFarmacia));
          if (farmaciaAvailableNCs.length === 0) return null;
          const totalDisponible = farmaciaAvailableNCs.reduce((sum, nc) => sum + nc.montoDisponible, 0);
          return <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-1172">
                      <div className="flex items-center gap-2 mb-3" data-id="element-1173">
                        <FileMinusIcon className="w-5 h-5 text-amber-600" data-id="element-1174" />
                        <h3 className="font-semibold text-amber-900" data-id="element-1175">
                          Notas de Crédito Disponibles para {appFarmacia} (
                          {farmaciaAvailableNCs.length})
                        </h3>
                      </div>
                      <div className="flex flex-wrap gap-2" data-id="element-1176">
                        {farmaciaAvailableNCs.map(nc => <div key={nc.id} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white text-amber-800 border border-amber-200" data-id="element-1177">
                            <span className="font-semibold" data-id="element-1178">{nc.numero}</span>
                            <span className="text-xs text-amber-600" data-id="element-1179">
                              {formatCurrency(nc.montoDisponible)}
                            </span>
                          </div>)}
                      </div>
                      <p className="text-xs text-amber-700 font-semibold mt-2" data-id="element-1180">
                        Total disponible: {formatCurrency(totalDisponible)}
                      </p>
                    </div>;
        })()}

              {/* Step 1: Select Farmacia */}
              <Card data-id="element-1181">
                <div className="flex items-center gap-3 mb-4" data-id="element-1182">
                  <div className="w-8 h-8 bg-[#2952C4] text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-1183">
                    1
                  </div>
                  <h3 className="font-semibold text-slate-900" data-id="element-1184">
                    Seleccionar Farmacia
                  </h3>
                </div>
                <SearchableSelect label="" value={appFarmacia} onChange={val => {
            setAppFarmacia(val);
            setSelectedInvoiceId(null);
            setSelectedNCId(null);
          }} options={FARMACIAS} placeholder="Buscar farmacia..." data-id="element-1185" />
              </Card>

              {appFarmacia && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1186">
                  {/* Step 2: Select Invoice */}
                  <Card data-id="element-1187">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1188">
                      <div className="w-8 h-8 bg-[#2952C4] text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-1189">
                        2
                      </div>
                      <h3 className="font-semibold text-slate-900" data-id="element-1190">
                        Seleccionar Factura
                      </h3>
                    </div>
                    {appInvoices.length === 0 ? <div className="p-8 text-center bg-slate-50 rounded-xl" data-id="element-1191">
                        <CheckCircleIcon className="w-8 h-8 text-emerald-400 mx-auto mb-2" data-id="element-1192" />
                        <p className="text-slate-600 font-medium" data-id="element-1193">
                          No hay facturas pendientes
                        </p>
                      </div> : <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1194">
                        <table className="w-full text-sm" data-id="element-1195">
                          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1196">
                            <tr data-id="element-1197">
                              <th className="px-3 py-2 w-10" data-id="element-1198"></th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-500" data-id="element-1199">
                                Nº Factura
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-500" data-id="element-1200">
                                Subtotal
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-500" data-id="element-1201">
                                ITBIS
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-500" data-id="element-1202">
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100" data-id="element-1203">
                            {appInvoices.map(inv => <tr key={inv.id} className={`cursor-pointer transition-colors ${selectedInvoiceId === inv.id ? 'bg-blue-50' : 'hover:bg-slate-50'}`} onClick={() => setSelectedInvoiceId(inv.id)} data-id="element-1204">
                                <td className="px-3 py-3 text-center" data-id="element-1205">
                                  <input type="radio" name="invoice" checked={selectedInvoiceId === inv.id} onChange={() => setSelectedInvoiceId(inv.id)} className="w-4 h-4 text-[#2952C4]" data-id="element-1206" />
                                </td>
                                <td className="px-3 py-3 font-medium text-slate-900" data-id="element-1207">
                                  {inv.numeroFactura}
                                </td>
                                <td className="px-3 py-3 text-right text-slate-700" data-id="element-1208">
                                  {formatCurrency(inv.subTotal)}
                                </td>
                                <td className="px-3 py-3 text-right text-slate-600" data-id="element-1209">
                                  {formatCurrency(inv.itbis)}
                                </td>
                                <td className="px-3 py-3 text-right font-bold text-slate-900" data-id="element-1210">
                                  {formatCurrency(inv.monto)}
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </div>}
                  </Card>

                  {/* Step 3: Select NC (optional) */}
                  <Card data-id="element-1211">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1212">
                      <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-1213">
                        3
                      </div>
                      <h3 className="font-semibold text-slate-900" data-id="element-1214">
                        Seleccionar Nota de Crédito{' '}
                        <span className="text-slate-400 font-normal" data-id="element-1215">
                          (opcional)
                        </span>
                      </h3>
                    </div>
                    {appCreditNotes.length === 0 ? <div className="p-8 text-center bg-slate-50 rounded-xl" data-id="element-1216">
                        <FileMinusIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-1217" />
                        <p className="text-slate-500 font-medium" data-id="element-1218">
                          No hay NC disponibles para esta farmacia
                        </p>
                      </div> : <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1219">
                        <table className="w-full text-sm" data-id="element-1220">
                          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1221">
                            <tr data-id="element-1222">
                              <th className="px-3 py-2 w-10" data-id="element-1223"></th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-500" data-id="element-1224">
                                Nº NC
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-500" data-id="element-1225">
                                Disponible
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100" data-id="element-1226">
                            {appCreditNotes.map(nc => <tr key={nc.id} className={`cursor-pointer transition-colors ${selectedNCId === nc.id ? 'bg-amber-50' : 'hover:bg-slate-50'}`} onClick={() => setSelectedNCId(selectedNCId === nc.id ? null : nc.id)} data-id="element-1227">
                                <td className="px-3 py-3 text-center" data-id="element-1228">
                                  <input type="checkbox" checked={selectedNCId === nc.id} onChange={() => setSelectedNCId(selectedNCId === nc.id ? null : nc.id)} className="w-4 h-4 text-amber-500 rounded" data-id="element-1229" />
                                </td>
                                <td className="px-3 py-3 font-medium text-slate-900" data-id="element-1230">
                                  {nc.numero}
                                </td>
                                <td className="px-3 py-3 text-right font-bold text-rose-600" data-id="element-1231">
                                  -{formatCurrency(nc.montoDisponible)}
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </div>}
                  </Card>
                </div>}

              {/* Calculation Breakdown */}
              {selectedInvoice && calculation && <Card className="border-2 border-[#2952C4]/20 bg-gradient-to-br from-blue-50/50 to-white" data-id="element-1232">
                  <div className="flex items-center gap-3 mb-6" data-id="element-1233">
                    <div className="w-10 h-10 bg-[#2952C4] text-white rounded-xl flex items-center justify-center" data-id="element-1234">
                      <CalculatorIcon className="w-5 h-5" data-id="element-1235" />
                    </div>
                    <div data-id="element-1236">
                      <h3 className="font-bold text-slate-900" data-id="element-1237">
                        Resumen de Cálculo
                      </h3>
                      <p className="text-sm text-slate-500" data-id="element-1238">
                        Factura {selectedInvoice.numeroFactura}{' '}
                        {selectedNC ? `+ NC ${selectedNC.numero}` : ''}
                      </p>
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-id="element-1239">
                    <table className="w-full" data-id="element-1240">
                      <tbody className="divide-y divide-slate-100" data-id="element-1241">
                        <tr className="bg-slate-50" data-id="element-1242">
                          <td className="px-5 py-3 text-sm text-slate-600" data-id="element-1243">
                            Subtotal original
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-slate-900" data-id="element-1244">
                            {formatCurrency(calculation.subtotalOriginal)}
                          </td>
                        </tr>
                        {calculation.ncMonto > 0 && <tr className="bg-rose-50" data-id="element-1245">
                            <td className="px-5 py-3 text-sm text-rose-700" data-id="element-1246">
                              Nota de crédito aplicada
                            </td>
                            <td className="px-5 py-3 text-right font-bold text-rose-600" data-id="element-1247">
                              ({formatCurrency(calculation.ncMonto)})
                            </td>
                          </tr>}
                        <tr data-id="element-1248">
                          <td className="px-5 py-3 text-sm text-slate-600" data-id="element-1249">
                            Subtotal ajustado
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-slate-900" data-id="element-1250">
                            {formatCurrency(calculation.subtotalAjustado)}
                          </td>
                        </tr>
                        {calculation.descuentoMonto > 0 && <tr className="bg-emerald-50" data-id="element-1251">
                            <td className="px-5 py-3 text-sm text-emerald-700" data-id="element-1252">
                              Descuento {calculation.descuentoPct}%
                            </td>
                            <td className="px-5 py-3 text-right font-bold text-emerald-600" data-id="element-1253">
                              ({formatCurrency(calculation.descuentoMonto)})
                            </td>
                          </tr>}
                        {calculation.descuentoMonto > 0 && <tr data-id="element-1254">
                            <td className="px-5 py-3 text-sm text-slate-600" data-id="element-1255">
                              Subtotal final
                            </td>
                            <td className="px-5 py-3 text-right font-bold text-slate-900" data-id="element-1256">
                              {formatCurrency(calculation.subtotalFinal)}
                            </td>
                          </tr>}
                        <tr className="bg-slate-50" data-id="element-1257">
                          <td className="px-5 py-3 text-sm text-slate-600" data-id="element-1258">
                            ITBIS (fijo)
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-slate-700" data-id="element-1259">
                            {formatCurrency(calculation.itbisFinal)}
                          </td>
                        </tr>
                        <tr className="bg-[#2952C4]" data-id="element-1260">
                          <td className="px-5 py-4 text-sm font-bold text-white" data-id="element-1261">
                            TOTAL A PAGAR
                          </td>
                          <td className="px-5 py-4 text-right text-xl font-bold text-white" data-id="element-1262">
                            {formatCurrency(calculation.totalAPagar)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-6 flex justify-end" data-id="element-1263">
                    <Button onClick={handleApplyNC} isLoading={isApplying} leftIcon={<CheckCircleIcon className="w-4 h-4" data-id="element-1265" />} data-id="element-1264">
                      Aplicar y Guardar
                    </Button>
                  </div>
                </Card>}
            </>}
        </>}

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: HISTORIAL NC */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'historial-nc' && (() => {
      // Filter NC applications by vendor — non-admins only see their own
      const filteredNcApps = isAdmin ? ncApplications : ncApplications.filter(ncApp => {
        // Match by invoice vendor
        const invoice = invoices.find(inv => inv.id === ncApp.invoice_id);
        if (invoice && invoice.vendedorId === user?.id) return true;
        // Fallback: match by credit note vendor
        const creditNote = creditNotes.find(nc => nc.id === ncApp.credit_note_id);
        if (creditNote && creditNote.vendedorId === user?.id) return true;
        // Fallback: match by aplicado_por name
        if (ncApp.aplicado_por === user?.nombre) return true;
        return false;
      });
      const ncAppTotalPages = Math.ceil(filteredNcApps.length / ITEMS_PER_PAGE);
      const paginatedNCApps = filteredNcApps.slice((ncAppPage - 1) * ITEMS_PER_PAGE, ncAppPage * ITEMS_PER_PAGE);
      return <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1266">
                <StatsCard title="Total Aplicaciones" value={filteredNcApps.length} icon={<ArrowRightLeftIcon className="w-5 h-5" data-id="element-1268" />} variant="primary" data-id="element-1267" />
                <StatsCard title="Monto Total Aplicado" value={formatCurrency(filteredNcApps.reduce((sum, nc) => sum + Number(nc.nc_monto_aplicado || 0), 0))} icon={<DollarSignIcon className="w-5 h-5" data-id="element-1270" />} variant="warning" data-id="element-1269" />
                <StatsCard title="Descuento Total" value={formatCurrency(filteredNcApps.reduce((sum, nc) => sum + Number(nc.descuento_monto || 0), 0))} icon={<PercentIcon className="w-5 h-5" data-id="element-1272" />} variant="success" data-id="element-1271" />
              </div>

              <Card padding="none" data-id="element-1273">
                {filteredNcApps.length === 0 ? <div className="p-16 text-center" data-id="element-1274">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-1275">
                      <ArrowRightLeftIcon className="w-8 h-8 text-slate-400" data-id="element-1276" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-1277">
                      No hay aplicaciones
                    </h3>
                    <p className="text-slate-500 text-sm" data-id="element-1278">
                      No se han aplicado notas de crédito a facturas aún
                    </p>
                  </div> : <>
                    {/* Mobile cards */}
                    <div className="md:hidden divide-y divide-slate-100" data-id="element-1279">
                      {paginatedNCApps.map(ncApp => <div key={ncApp.id} className="p-4" data-id="element-1280">
                          <div className="flex items-start justify-between gap-2" data-id="element-1281">
                            <div className="flex-1 min-w-0" data-id="element-1282">
                              <p className="font-semibold text-slate-900 text-sm" data-id="element-1283">
                                NC: {ncApp.credit_note_numero}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5" data-id="element-1284">
                                → Factura: {ncApp.invoice_numero}
                              </p>
                              <p className="text-xs text-slate-400 mt-0.5" data-id="element-1285">
                                {ncApp.farmacia}
                              </p>
                            </div>
                            <button onClick={() => setAnularNCApp(ncApp)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors flex-shrink-0" title="Anular aplicación" data-id="element-1286">
                              <UndoIcon className="w-4 h-4" data-id="element-1287" />
                            </button>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-2 text-xs" data-id="element-1288">
                            <div className="bg-rose-50 rounded-lg p-2" data-id="element-1289">
                              <p className="text-rose-500 mb-0.5" data-id="element-1290">
                                NC Aplicada
                              </p>
                              <p className="font-bold text-rose-700" data-id="element-1291">
                                -
                                {formatCurrency(Number(ncApp.nc_monto_aplicado || 0))}
                              </p>
                            </div>
                            <div className="bg-emerald-50 rounded-lg p-2" data-id="element-1292">
                              <p className="text-emerald-500 mb-0.5" data-id="element-1293">
                                Desc. {Number(ncApp.descuento_pct || 0)}%
                              </p>
                              <p className="font-bold text-emerald-700" data-id="element-1294">
                                -
                                {formatCurrency(Number(ncApp.descuento_monto || 0))}
                              </p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-2" data-id="element-1295">
                              <p className="text-slate-400 mb-0.5" data-id="element-1296">
                                Total Original
                              </p>
                              <p className="font-bold text-slate-700" data-id="element-1297">
                                {formatCurrency(Number(ncApp.total_original || 0))}
                              </p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-2" data-id="element-1298">
                              <p className="text-blue-500 mb-0.5" data-id="element-1299">
                                Total a Pagar
                              </p>
                              <p className="font-bold text-blue-700" data-id="element-1300">
                                {formatCurrency(Number(ncApp.total_a_pagar || 0))}
                              </p>
                            </div>
                          </div>
                          <p className="text-xs text-slate-400 mt-2" data-id="element-1301">
                            {new Date(ncApp.fecha_aplicacion || ncApp.created_at).toLocaleDateString('es-DO', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
                            {' · '}Por: {ncApp.aplicado_por}
                          </p>
                        </div>)}
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto" data-id="element-1302">
                      <table className="w-full" data-id="element-1303">
                        <thead data-id="element-1304">
                          <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-1305">
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1306">
                              Fecha
                            </th>
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1307">
                              NC
                            </th>
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1308">
                              Factura
                            </th>
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1309">
                              Farmacia
                            </th>
                            <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-1310">
                              Total Original
                            </th>
                            <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-1311">
                              NC Aplicada
                            </th>
                            <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-1312">
                              Descuento
                            </th>
                            <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-1313">
                              Total a Pagar
                            </th>
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1314">
                              Aplicado Por
                            </th>
                            <th className="px-5 py-4 w-16" data-id="element-1315"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100" data-id="element-1316">
                          {paginatedNCApps.map(ncApp => <tr key={ncApp.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-1317">
                              <td className="px-5 py-4 text-sm text-slate-600" data-id="element-1318">
                                {new Date(ncApp.fecha_aplicacion || ncApp.created_at).toLocaleDateString('es-DO', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                              </td>
                              <td className="px-5 py-4" data-id="element-1319">
                                <span className="font-semibold text-slate-900 text-sm" data-id="element-1320">
                                  {ncApp.credit_note_numero}
                                </span>
                              </td>
                              <td className="px-5 py-4" data-id="element-1321">
                                <span className="font-medium text-slate-700 text-sm" data-id="element-1322">
                                  {ncApp.invoice_numero}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-sm text-slate-600" data-id="element-1323">
                                {ncApp.farmacia}
                              </td>
                              <td className="px-5 py-4 text-right text-sm text-slate-700" data-id="element-1324">
                                {formatCurrency(Number(ncApp.total_original || 0))}
                              </td>
                              <td className="px-5 py-4 text-right" data-id="element-1325">
                                <span className="text-sm font-semibold text-rose-600" data-id="element-1326">
                                  -
                                  {formatCurrency(Number(ncApp.nc_monto_aplicado || 0))}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-right" data-id="element-1327">
                                <span className="text-sm font-semibold text-emerald-600" data-id="element-1328">
                                  -
                                  {formatCurrency(Number(ncApp.descuento_monto || 0))}
                                </span>
                                {Number(ncApp.descuento_pct) > 0 && <span className="text-xs text-slate-400 ml-1" data-id="element-1329">
                                    ({ncApp.descuento_pct}%)
                                  </span>}
                              </td>
                              <td className="px-5 py-4 text-right" data-id="element-1330">
                                <span className="text-sm font-bold text-[#2952C4]" data-id="element-1331">
                                  {formatCurrency(Number(ncApp.total_a_pagar || 0))}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-sm text-slate-500" data-id="element-1332">
                                {ncApp.aplicado_por}
                              </td>
                              <td className="px-5 py-4" data-id="element-1333">
                                <button onClick={() => setAnularNCApp(ncApp)} title="Anular aplicación de NC" className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-1334">
                                  <UndoIcon className="w-4 h-4" data-id="element-1335" />
                                </button>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </>}
              </Card>
              {filteredNcApps.length > ITEMS_PER_PAGE && <Pagination currentPage={ncAppPage} totalPages={ncAppTotalPages} onPageChange={setNcAppPage} totalItems={filteredNcApps.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-1336" />}
            </>;
    })()}

      {/* Anular NC Application Modal */}
      <Modal isOpen={!!anularNCApp} onClose={() => setAnularNCApp(null)} title="Anular Aplicación de Nota de Crédito" size="sm" data-id="element-1337">
        <ModalBody data-id="element-1338">
          <div className="text-center space-y-3" data-id="element-1339">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-rose-50 rounded-2xl" data-id="element-1340">
              <UndoIcon className="w-7 h-7 text-rose-500" data-id="element-1341" />
            </div>
            <p className="text-slate-600" data-id="element-1342">
              ¿Estás seguro de anular la aplicación de la nota de crédito{' '}
              <strong data-id="element-1343">{anularNCApp?.credit_note_numero}</strong> a la factura{' '}
              <strong data-id="element-1344">{anularNCApp?.invoice_numero}</strong>?
            </p>
            <div className="bg-slate-50 rounded-xl p-3 text-sm space-y-1" data-id="element-1345">
              <p className="text-slate-500" data-id="element-1346">
                Farmacia:{' '}
                <strong className="text-slate-700" data-id="element-1347">
                  {anularNCApp?.farmacia}
                </strong>
              </p>
              <p className="text-slate-500" data-id="element-1348">
                NC aplicada:{' '}
                <strong className="text-rose-600" data-id="element-1349">
                  -{formatCurrency(Number(anularNCApp?.nc_monto_aplicado || 0))}
                </strong>
              </p>
              <p className="text-slate-500" data-id="element-1350">
                Descuento:{' '}
                <strong className="text-emerald-600" data-id="element-1351">
                  -{formatCurrency(Number(anularNCApp?.descuento_monto || 0))}
                </strong>
              </p>
            </div>
            <p className="text-xs text-slate-400" data-id="element-1352">
              Se restaurará el saldo de la factura y el monto disponible de la
              nota de crédito.
            </p>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-1353">
          <Button variant="secondary" onClick={() => setAnularNCApp(null)} data-id="element-1354">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleAnularNCApplication} isLoading={isAnulandoNC} leftIcon={<UndoIcon className="w-4 h-4" data-id="element-1356" />} data-id="element-1355">
            Anular Aplicación
          </Button>
        </ModalFooter>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* MODALS */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}

      {/* Invoice Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingInvoice ? 'Editar Factura' : 'Nueva Factura'} size="lg" data-id="element-1357">
        <ModalBody data-id="element-1358">
          <div className="space-y-4" data-id="element-1359">
            {facturaError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-1360">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-1361" />
                {facturaError}
              </div>}
            {!editingInvoice && <button type="button" onClick={() => setIsCotizacionModalOpen(true)} className="group w-full relative overflow-hidden rounded-2xl border-2 border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 text-left transition-all duration-300 hover:border-indigo-300 hover:shadow-lg hover:shadow-indigo-100/50 hover:scale-[1.01] active:scale-[0.99]" data-id="element-1362">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-indigo-100/60 to-transparent rounded-bl-full -translate-y-4 translate-x-4 group-hover:translate-x-2 group-hover:-translate-y-2 transition-transform duration-500" data-id="element-1363" />
                <div className="relative flex items-center gap-4" data-id="element-1364">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-md shadow-indigo-200/60 group-hover:shadow-lg group-hover:shadow-indigo-300/60 transition-shadow duration-300" data-id="element-1365">
                    <SearchIcon className="w-5 h-5 text-white" data-id="element-1366" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1367">
                    <p className="font-bold text-slate-900 text-sm group-hover:text-indigo-900 transition-colors" data-id="element-1368">
                      Buscar Cotización (Pedido)
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5 group-hover:text-indigo-400 transition-colors" data-id="element-1369">
                      Importar datos desde un pedido existente
                    </p>
                  </div>
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors" data-id="element-1370">
                    <svg className="w-4 h-4 text-indigo-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-all duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} data-id="element-1371">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" data-id="element-1372" />
                    </svg>
                  </div>
                </div>
              </button>}
            <div className="grid grid-cols-2 gap-4" data-id="element-1373">
              <Input label="Número de Factura" value={formData.numeroFactura} onChange={e => {
              setFacturaError('');
              setFormData({
                ...formData,
                numeroFactura: e.target.value
              });
            }} placeholder="FAC-2024-001" data-id="element-1374" />
              <div data-id="element-1375">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1376">
                  Estado
                </label>
                <select value={formData.estado} onChange={e => {
                const newEstado = e.target.value as Invoice['estado'];
                setFormData({
                  ...formData,
                  estado: newEstado
                });
              }} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1377">
                  <option value="pendiente" data-id="element-1378">Pendiente</option>
                  <option value="parcial" data-id="element-1379">Pago Parcial</option>
                  <option value="vencida" data-id="element-1380">Vencida</option>
                  <option value="pagada" data-id="element-1381">Pagada</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-1382">
              <SearchableSelect label="Farmacia" value={formData.farmacia} onChange={val => setFormData({
              ...formData,
              farmacia: val
            })} options={FARMACIAS} placeholder="Buscar farmacia..." data-id="element-1383" />
              <div data-id="element-1384">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1385">
                  Vendedor
                </label>
                <select value={formData.vendedorId} onChange={e => {
                const selectedUser = users.find(u => u.id === e.target.value);
                setFormData({
                  ...formData,
                  vendedorId: e.target.value,
                  vendedor: selectedUser?.nombre || ''
                });
              }} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1386">
                  <option value="" data-id="element-1387">Seleccionar vendedor...</option>
                  {users.filter(u => u.activo && u.rolNombre !== 'Administrador' && u.permisos?.['new-visit'] === true).map(u => <option key={u.id} value={u.id} data-id="element-1388">
                        {u.nombre}
                      </option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-1389">
              <Input label="Fecha de Emisión" type="date" value={formData.fechaEmision} onChange={e => setFormData({
              ...formData,
              fechaEmision: e.target.value
            })} data-id="element-1390" />
              <Input label="Fecha de Vencimiento" type="date" value={formData.fechaVencimiento} min={!editingInvoice ? new Date().toISOString().split('T')[0] : undefined} onChange={e => {
              const newFechaVenc = e.target.value;
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const fechaVenc = new Date(newFechaVenc);
              const saldoPend = Number(formData.saldoPendiente);
              const monto = Number(formData.monto);
              let newEstado = formData.estado;
              if (formData.estado === 'vencida' && fechaVenc >= today) {
                // Fecha movida al futuro → ya no está vencida
                newEstado = saldoPend < monto && saldoPend > 0 ? 'parcial' : 'pendiente';
              } else if (formData.estado !== 'pagada' && fechaVenc < today && saldoPend > 0) {
                // Fecha movida al pasado → marcar como vencida
                newEstado = 'vencida';
              }
              setFormData({
                ...formData,
                fechaVencimiento: newFechaVenc,
                estado: newEstado
              });
            }} data-id="element-1391" />
            </div>
            <div className="grid grid-cols-3 gap-4" data-id="element-1392">
              <Input label="Sub Total (DOP)" type="number" value={formData.subTotal} onChange={e => {
              const sub = Number(e.target.value) || 0;
              const tax = Math.round(sub * 0.18 * 100) / 100;
              const total = Math.round((sub + tax) * 100) / 100;
              setFormData({
                ...formData,
                subTotal: e.target.value,
                itbis: String(tax),
                monto: String(total),
                saldoPendiente: editingInvoice ? formData.saldoPendiente : String(total)
              });
            }} placeholder="0" data-id="element-1393" />
              <Input label="ITBIS 18% (DOP)" type="number" value={formData.itbis} onChange={e => {
              const tax = Number(e.target.value) || 0;
              const sub = Number(formData.subTotal) || 0;
              const total = Math.round((sub + tax) * 100) / 100;
              setFormData({
                ...formData,
                itbis: e.target.value,
                monto: String(total),
                saldoPendiente: editingInvoice ? formData.saldoPendiente : String(total)
              });
            }} placeholder="0" data-id="element-1394" />
              <div data-id="element-1395">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1396">
                  Monto Total (DOP)
                </label>
                <div className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 font-bold" data-id="element-1397">
                  {formatCurrency(Number(formData.monto) || 0)}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-1398">
              <Input label="Saldo Pendiente (DOP)" type="number" value={formData.saldoPendiente} onChange={e => setFormData({
              ...formData,
              saldoPendiente: e.target.value
            })} placeholder="0" data-id="element-1399" />
              <div data-id="element-1400">
                <Input label="PP % (Pronto Pago)" type="number" value={formData.ppPct} onChange={e => setFormData({
                ...formData,
                ppPct: e.target.value
              })} placeholder={`Global: ${discountPct}%`} data-id="element-1401" />
                <p className="text-xs text-slate-400 mt-1" data-id="element-1402">
                  Dejar vacío para usar el % global ({discountPct}%)
                </p>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-1403">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-1404">
            Cancelar
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} disabled={!formData.numeroFactura || !formData.monto || !formData.fechaVencimiento} data-id="element-1405">
            {editingInvoice ? 'Guardar Cambios' : 'Crear Factura'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Invoice Delete Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Factura" size="sm" data-id="element-1406">
        <ModalBody data-id="element-1407">
          <p className="text-slate-600" data-id="element-1408">
            ¿Eliminar la factura{' '}
            <strong data-id="element-1409">{editingInvoice?.numeroFactura}</strong>? Esta acción no se
            puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-1410">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-1411">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} data-id="element-1412">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>

      {/* NC Add/Edit Modal */}
      <Modal isOpen={isNCModalOpen} onClose={() => setIsNCModalOpen(false)} title={editingNC ? 'Editar Nota de Crédito' : 'Nueva Nota de Crédito'} size="md" data-id="element-1413">
        <ModalBody data-id="element-1414">
          <div className="space-y-4" data-id="element-1415">
            {ncError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-1416">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-1417" />
                {ncError}
              </div>}
            <Input label="Número de NC" value={ncFormData.numero} onChange={e => {
            setNCError('');
            setNCFormData({
              ...ncFormData,
              numero: e.target.value
            });
          }} placeholder="NC-00001" data-id="element-1418" />
            <SearchableSelect label="Farmacia" value={ncFormData.farmacia} onChange={val => setNCFormData({
            ...ncFormData,
            farmacia: val
          })} options={FARMACIAS} placeholder="Buscar farmacia..." data-id="element-1419" />
            <div data-id="element-1420">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1421">
                Vendedor
              </label>
              <select value={ncFormData.vendedorId} onChange={e => {
              const selectedUser = users.find(u => u.id === e.target.value);
              setNCFormData({
                ...ncFormData,
                vendedorId: e.target.value,
                vendedor: selectedUser?.nombre || ''
              });
            }} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1422">
                <option value="" data-id="element-1423">Seleccionar vendedor...</option>
                {users.filter(u => u.activo && u.rolNombre !== 'Administrador' && u.permisos?.['new-visit'] === true).map(u => <option key={u.id} value={u.id} data-id="element-1424">
                      {u.nombre}
                    </option>)}
              </select>
            </div>
            <Input label="Monto (DOP)" type="number" value={ncFormData.monto} onChange={e => setNCFormData({
            ...ncFormData,
            monto: e.target.value
          })} placeholder="0" data-id="element-1425" />
            <div data-id="element-1426">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1427">
                Notas
              </label>
              <textarea value={ncFormData.notas} onChange={e => setNCFormData({
              ...ncFormData,
              notas: e.target.value
            })} rows={2} className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Notas opcionales..." data-id="element-1428" />
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-1429">
          <Button variant="secondary" onClick={() => setIsNCModalOpen(false)} data-id="element-1430">
            Cancelar
          </Button>
          <Button onClick={handleSaveNC} isLoading={isSaving} disabled={!ncFormData.numero || !ncFormData.monto || !ncFormData.farmacia} data-id="element-1431">
            {editingNC ? 'Guardar Cambios' : 'Crear NC'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* NC Delete Modal */}
      <Modal isOpen={isNCDeleteModalOpen} onClose={() => setIsNCDeleteModalOpen(false)} title="Eliminar Nota de Crédito" size="sm" data-id="element-1432">
        <ModalBody data-id="element-1433">
          <p className="text-slate-600" data-id="element-1434">
            ¿Eliminar la NC <strong data-id="element-1435">{editingNC?.numero}</strong>? Esta acción no
            se puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-1436">
          <Button variant="secondary" onClick={() => setIsNCDeleteModalOpen(false)} data-id="element-1437">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDeleteNC} data-id="element-1438">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Payment Modal */}
      <Modal isOpen={isPaymentModalOpen} onClose={() => {
      setIsPaymentModalOpen(false);
      setPaymentDone(false);
    }} title={paymentDone ? '✓ Pago Registrado' : 'Registrar Pago Múltiple'} size="lg" data-id="element-1439">
        <ModalBody data-id="element-1440">
          {paymentDone && lastPaymentData ? <div className="space-y-5" data-id="element-1441">
              <div className="flex flex-col items-center text-center p-6 bg-emerald-50 border border-emerald-200 rounded-2xl" data-id="element-1442">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4" data-id="element-1443">
                  <CheckCircleIcon className="w-9 h-9 text-emerald-600" data-id="element-1444" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-1445">
                  ¡Pago procesado exitosamente!
                </h3>
                <p className="text-sm text-slate-600" data-id="element-1446">
                  Se registró un pago de{' '}
                  <strong className="text-emerald-700" data-id="element-1447">
                    {formatCurrency(lastPaymentData.total)}
                  </strong>{' '}
                  para <strong data-id="element-1448">{lastPaymentData.farmacia}</strong>
                </p>
                <div className="mt-3 flex flex-wrap gap-2 justify-center" data-id="element-1449">
                  {lastPaymentData.invoices.map(inv => <span key={inv.numeroFactura} className="px-2.5 py-1 bg-white border border-emerald-200 rounded-lg text-xs font-semibold text-emerald-700" data-id="element-1450">
                      {inv.numeroFactura}: {formatCurrency(inv.abono)}
                    </span>)}
                </div>
              </div>
              <div data-id="element-1451">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1452">
                  Método de pago (para el recibo)
                </label>
                <select value={paymentMetodo} onChange={e => setPaymentMetodo(e.target.value)} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1453">
                  <option value="efectivo" data-id="element-1454">Efectivo</option>
                  <option value="cheque" data-id="element-1455">Cheque</option>
                  <option value="transferencia" data-id="element-1456">Transferencia</option>
                </select>
              </div>
              <button onClick={handleDownloadRecibo} disabled={isDownloadingRecibo} className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#2952C4] hover:bg-[#1E3D96] disabled:opacity-60 text-white font-bold text-base rounded-xl transition-colors shadow-lg" data-id="element-1457">
                {isDownloadingRecibo ? <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" data-id="element-1458" /> : <DownloadIcon className="w-5 h-5" data-id="element-1459" />}
                {isDownloadingRecibo ? 'Generando PDF...' : 'Descargar Recibo PDF'}
              </button>
            </div> : <div className="space-y-6" data-id="element-1460">
              <SearchableSelect label="Seleccionar Cliente" value={paymentClient} onChange={val => {
            setPaymentClient(val);
            setPaymentAmounts({});
          }} options={FARMACIAS} placeholder="Buscar farmacia..." data-id="element-1461" />
              {paymentClient && (clientInvoices.length === 0 ? <div className="p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200" data-id="element-1462">
                    <CheckCircleIcon className="w-8 h-8 text-emerald-400 mx-auto mb-2" data-id="element-1463" />
                    <p className="text-slate-600 font-medium" data-id="element-1464">
                      Este cliente no tiene facturas pendientes
                    </p>
                  </div> : <div className="space-y-4" data-id="element-1465">
                    <div className="flex items-center justify-between" data-id="element-1466">
                      <h3 className="text-sm font-semibold text-slate-900" data-id="element-1467">
                        Facturas Pendientes
                      </h3>
                      <span className="text-xs text-slate-500" data-id="element-1468">
                        Ingrese el monto a pagar por factura
                      </span>
                    </div>
                    <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1469">
                      <table className="w-full text-sm" data-id="element-1470">
                        <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1471">
                          <tr data-id="element-1472">
                            <th className="px-4 py-3 text-left font-semibold text-slate-500" data-id="element-1473">
                              Factura
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-500" data-id="element-1474">
                              Vencimiento
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-500" data-id="element-1475">
                              Saldo
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-500 w-40" data-id="element-1476">
                              Abono
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100" data-id="element-1477">
                          {clientInvoices.map(inv => <tr key={inv.id} className="hover:bg-slate-50/50" data-id="element-1478">
                              <td className="px-4 py-3 font-medium text-slate-900" data-id="element-1479">
                                {inv.numeroFactura}
                              </td>
                              <td className="px-4 py-3 text-slate-600" data-id="element-1480">
                                {formatDate(inv.fechaVencimiento)}
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-slate-900" data-id="element-1481">
                                {formatCurrency(inv.saldoPendiente)}
                              </td>
                              <td className="px-4 py-3" data-id="element-1482">
                                <div className="relative" data-id="element-1483">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-1484">
                                    $
                                  </span>
                                  <input type="number" min="0" max={inv.saldoPendiente} placeholder="0" value={paymentAmounts[inv.id] || ''} onChange={e => handlePaymentAmountChange(inv.id, e.target.value)} className="w-full pl-6 pr-3 py-1.5 text-right border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-900" data-id="element-1485" />
                                </div>
                              </td>
                            </tr>)}
                        </tbody>
                        <tfoot className="bg-slate-50 border-t border-slate-200" data-id="element-1486">
                          <tr data-id="element-1487">
                            <td colSpan={3} className="px-4 py-3 text-right font-bold text-slate-700" data-id="element-1488">
                              Total a Pagar:
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-emerald-600 text-lg" data-id="element-1489">
                              {formatCurrency(totalPayment)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>)}
            </div>}
        </ModalBody>
        <ModalFooter data-id="element-1490">
          {paymentDone ? <Button variant="secondary" onClick={() => {
          setIsPaymentModalOpen(false);
          setPaymentDone(false);
        }} data-id="element-1491">
              Cerrar
            </Button> : <>
              <Button variant="secondary" onClick={() => setIsPaymentModalOpen(false)} data-id="element-1492">
                Cancelar
              </Button>
              <Button onClick={handleProcessPayment} isLoading={isSaving} disabled={!paymentClient || totalPayment <= 0} data-id="element-1493">
                Procesar Pago
              </Button>
            </>}
        </ModalFooter>
      </Modal>

      {/* Cotizacion Selection Modal */}
      <Modal isOpen={isCotizacionModalOpen} onClose={() => setIsCotizacionModalOpen(false)} title="Seleccionar Cotización" size="lg" backdropBlur stacked data-id="element-1494">
        <ModalBody data-id="element-1495">
          <div className="space-y-4" data-id="element-1496">
            <div className="relative" data-id="element-1497">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1498" />
              <input type="text" placeholder="Buscar por farmacia o número de pedido..." value={cotizacionSearch} onChange={e => setCotizacionSearch(e.target.value)} className="w-full pl-9 pr-4 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-slate-50/50" data-id="element-1499" />
            </div>

            <div className="max-h-[28rem] overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100" data-id="element-1500">
              {orders.filter(o => o.farmacia.toLowerCase().includes(cotizacionSearch.toLowerCase()) || o.numeroPedido.toLowerCase().includes(cotizacionSearch.toLowerCase())).map(order => <button key={order.id} onClick={() => {
              const sub = Math.round(order.total / 1.18 * 100) / 100;
              const tax = Math.round((order.total - sub) * 100) / 100;
              const matchedUser = users.find(u => u.nombre === order.vendedor);
              setFormData({
                ...formData,
                farmacia: order.farmacia,
                vendedor: order.vendedor,
                vendedorId: matchedUser?.id || '',
                subTotal: String(sub),
                itbis: String(tax),
                monto: String(order.total),
                saldoPendiente: String(order.total)
              });
              setIsCotizacionModalOpen(false);
            }} className="w-full text-left p-4 hover:bg-slate-50 transition-colors flex items-center justify-between" data-id="element-1501">
                    <div data-id="element-1502">
                      <p className="font-semibold text-slate-900" data-id="element-1503">
                        {order.farmacia}
                      </p>
                      <p className="text-sm text-slate-500" data-id="element-1504">
                        {order.numeroPedido} •{' '}
                        {new Date(order.fecha).toLocaleDateString('es-DO')} •{' '}
                        {order.vendedor}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-1505">
                      <p className="font-bold text-indigo-600" data-id="element-1506">
                        RD$
                        {order.total.toLocaleString('es-DO', {
                    minimumFractionDigits: 2
                  })}
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-1507">
                        {order.items} items
                      </p>
                    </div>
                  </button>)}
              {orders.filter(o => o.farmacia.toLowerCase().includes(cotizacionSearch.toLowerCase()) || o.numeroPedido.toLowerCase().includes(cotizacionSearch.toLowerCase())).length === 0 && <div className="p-8 text-center text-slate-500" data-id="element-1508">
                  No se encontraron cotizaciones
                </div>}
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-1509">
          <Button variant="secondary" onClick={() => setIsCotizacionModalOpen(false)} data-id="element-1510">
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Discount Config Modal */}
      {canSeeDiscount && <Modal isOpen={isDiscountConfigOpen} onClose={() => setIsDiscountConfigOpen(false)} title="Configurar Descuento por Pronto Pago" size="sm" data-id="element-1511">
          <ModalBody data-id="element-1512">
            <div className="space-y-4" data-id="element-1513">
              <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl text-sm text-emerald-800" data-id="element-1514">
                El descuento se aplica automáticamente al usar "Aplicar NC".
              </div>
              <div data-id="element-1515">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1516">
                  Porcentaje de descuento (%)
                </label>
                <input type="number" min="0.1" max="50" step="0.5" value={discountConfigDraft.pct} onChange={e => setDiscountConfigDraft({
              ...discountConfigDraft,
              pct: Number(e.target.value)
            })} className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1517" />
              </div>
              <div data-id="element-1518">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1519">
                  Días máximos desde emisión
                </label>
                <input type="number" min="1" max="365" value={discountConfigDraft.days} onChange={e => setDiscountConfigDraft({
              ...discountConfigDraft,
              days: Number(e.target.value)
            })} className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1520" />
              </div>
              <div className="p-3 bg-slate-50 rounded-xl text-sm text-slate-600 text-center" data-id="element-1521">
                Descuento de{' '}
                <strong className="text-emerald-700" data-id="element-1522">
                  {discountConfigDraft.pct}%
                </strong>{' '}
                para cobros antes de los{' '}
                <strong className="text-emerald-700" data-id="element-1523">
                  {discountConfigDraft.days} días
                </strong>
              </div>
            </div>
          </ModalBody>
          <ModalFooter data-id="element-1524">
            <Button variant="secondary" onClick={() => setIsDiscountConfigOpen(false)} data-id="element-1525">
              Cancelar
            </Button>
            <Button onClick={() => {
          saveDiscountConfig(discountConfigDraft.pct, discountConfigDraft.days);
          addActivityLog('Editó descuento pronto pago', `Porcentaje: ${discountConfigDraft.pct}% | Días: ${discountConfigDraft.days}`);
          setIsDiscountConfigOpen(false);
        }} data-id="element-1526">
              Guardar Configuración
            </Button>
          </ModalFooter>
        </Modal>}
    </div>;
}